"use client"

import { useEffect, useRef } from "react"
import { Renderer, Program, Mesh, Triangle } from "ogl"
import "./TheGradient.css"

type RgbTuple = [number, number, number]

type gradientProps = {
  timeSpeed?: number
  colorBalance?: number
  warpStrength?: number
  warpFrequency?: number
  warpSpeed?: number
  warpAmplitude?: number
  blendAngle?: number
  blendSoftness?: number
  rotationAmount?: number
  noiseScale?: number
  grainAmount?: number
  grainScale?: number
  grainAnimated?: boolean
  contrast?: number
  gamma?: number
  saturation?: number
  centerX?: number
  centerY?: number
  zoom?: number
  color1?: string
  color2?: string
  color3?: string
  className?: string
}

const fallbackRgb: RgbTuple = [1, 1, 1]

const clamp01 = (value: number) => Math.min(1, Math.max(0, value))

const clamp255 = (value: number) => Math.min(255, Math.max(0, value))

const parseHexColor = (value: string): RgbTuple | null => {
  const hex = value.trim().replace(/^#/, "")
  const normalized =
    hex.length === 3 && /^[\da-f]{3}$/i.test(hex)
      ? `${hex[0]}${hex[0]}${hex[1]}${hex[1]}${hex[2]}${hex[2]}`
      : hex
  if (!/^[\da-f]{6}$/i.test(normalized)) {
    return null
  }
  return [
    parseInt(normalized.slice(0, 2), 16) / 255,
    parseInt(normalized.slice(2, 4), 16) / 255,
    parseInt(normalized.slice(4, 6), 16) / 255,
  ]
}

const parseRgbFunctionColor = (value: string): RgbTuple | null => {
  const trimmed = value.trim().toLowerCase()
  if (!trimmed.startsWith("rgb(") && !trimmed.startsWith("rgba(")) {
    return null
  }

  const bodyMatch = /^[^(]+\((.*)\)$/.exec(trimmed)
  if (!bodyMatch) {
    return null
  }

  const tokens = bodyMatch[1]
    .replace("/", " ")
    .split(/[,\s]+/)
    .map((token) => token.trim())
    .filter(Boolean)

  if (tokens.length < 3) {
    return null
  }

  const rgb = tokens.slice(0, 3).map((token) => {
    if (token.endsWith("%")) {
      const percent = Number(token.slice(0, -1))
      return Number.isFinite(percent) ? (percent / 100) * 255 : NaN
    }
    return Number(token)
  })

  if (rgb.some((channel) => !Number.isFinite(channel))) {
    return null
  }

  return [
    clamp255(rgb[0]) / 255,
    clamp255(rgb[1]) / 255,
    clamp255(rgb[2]) / 255,
  ]
}

const parseOklchColor = (value: string): RgbTuple | null => {
  const match =
    /^oklch\(\s*([+-]?(?:\d+\.?\d*|\.\d+))(%)?\s+([+-]?(?:\d+\.?\d*|\.\d+))\s+([+-]?(?:\d+\.?\d*|\.\d+))(deg|rad|grad|turn)?(?:\s*\/\s*[^\)]*)?\)$/i.exec(
      value.trim(),
    )
  if (!match) {
    return null
  }

  const [, lRaw, lUnit, cRaw, hRaw, hUnit] = match
  const l = Number(lRaw)
  const c = Number(cRaw)
  const h = Number(hRaw)

  if (!Number.isFinite(l) || !Number.isFinite(c) || !Number.isFinite(h)) {
    return null
  }

  const lightness = lUnit === "%" ? l / 100 : l
  let hueDegrees = h
  if (hUnit === "rad") {
    hueDegrees = (h * 180) / Math.PI
  } else if (hUnit === "grad") {
    hueDegrees = h * 0.9
  } else if (hUnit === "turn") {
    hueDegrees = h * 360
  }

  const hueRadians = (hueDegrees * Math.PI) / 180
  const a = c * Math.cos(hueRadians)
  const b = c * Math.sin(hueRadians)

  const lPrime = lightness + 0.3963377774 * a + 0.2158037573 * b
  const mPrime = lightness - 0.1055613458 * a - 0.0638541728 * b
  const sPrime = lightness - 0.0894841775 * a - 1.291485548 * b

  const lCube = lPrime * lPrime * lPrime
  const mCube = mPrime * mPrime * mPrime
  const sCube = sPrime * sPrime * sPrime

  const linearR = 4.0767416621 * lCube - 3.3077115913 * mCube + 0.2309699292 * sCube
  const linearG = -1.2684380046 * lCube + 2.6097574011 * mCube - 0.3413193965 * sCube
  const linearB = -0.0041960863 * lCube - 0.7034186147 * mCube + 1.707614701 * sCube

  const linearToSrgb = (channel: number) =>
    channel <= 0.0031308
      ? 12.92 * channel
      : 1.055 * Math.pow(channel, 1 / 2.4) - 0.055

  return [
    clamp01(linearToSrgb(linearR)),
    clamp01(linearToSrgb(linearG)),
    clamp01(linearToSrgb(linearB)),
  ]
}

const resolveCssVarColor = (value: string): string | null => {
  if (typeof document === "undefined") {
    return null
  }

  const match = /^var\(\s*(--[a-z0-9_-]+)\s*(?:,\s*(.+))?\)$/i.exec(
    value.trim(),
  )
  if (!match) {
    return null
  }

  const [, cssVarName, fallback] = match
  const resolved = getComputedStyle(document.documentElement)
    .getPropertyValue(cssVarName)
    .trim()
  if (resolved) {
    return resolved
  }

  const fallbackColor = fallback?.trim()
  return fallbackColor || null
}

const resolveBrowserColor = (value: string): string | null => {
  if (typeof document === "undefined") {
    return null
  }

  const probe = document.createElement("span")
  probe.style.color = ""
  probe.style.color = value
  if (!probe.style.color) {
    return null
  }

  probe.style.position = "absolute"
  probe.style.visibility = "hidden"
  probe.style.pointerEvents = "none"
  probe.style.inset = "0"

  document.documentElement.appendChild(probe)
  const resolved = getComputedStyle(probe).color
  probe.remove()

  return resolved || null
}

const colorToRgb = (value: string, depth = 0): RgbTuple => {
  if (!value || depth > 4) {
    return fallbackRgb
  }

  const direct =
    parseHexColor(value) ?? parseRgbFunctionColor(value) ?? parseOklchColor(value)
  if (direct) {
    return direct
  }

  const resolvedVar = resolveCssVarColor(value)
  if (resolvedVar) {
    return colorToRgb(resolvedVar, depth + 1)
  }

  const resolvedBrowserColor = resolveBrowserColor(value)
  if (resolvedBrowserColor && resolvedBrowserColor !== value) {
    return colorToRgb(resolvedBrowserColor, depth + 1)
  }

  return fallbackRgb
}

const vertex = `#version 300 es
in vec2 position;
void main() {
  gl_Position = vec4(position, 0.0, 1.0);
}
`

const fragment = `#version 300 es
precision highp float;
uniform vec2 iResolution;
uniform float iTime;
uniform float uTimeSpeed;
uniform float uColorBalance;
uniform float uWarpStrength;
uniform float uWarpFrequency;
uniform float uWarpSpeed;
uniform float uWarpAmplitude;
uniform float uBlendAngle;
uniform float uBlendSoftness;
uniform float uRotationAmount;
uniform float uNoiseScale;
uniform float uGrainAmount;
uniform float uGrainScale;
uniform float uGrainAnimated;
uniform float uContrast;
uniform float uGamma;
uniform float uSaturation;
uniform vec2 uCenterOffset;
uniform float uZoom;
uniform vec3 uColor1;
uniform vec3 uColor2;
uniform vec3 uColor3;
out vec4 fragColor;
#define S(a,b,t) smoothstep(a,b,t)
mat2 Rot(float a){float s=sin(a),c=cos(a);return mat2(c,-s,s,c);} 
vec2 hash(vec2 p){p=vec2(dot(p,vec2(2127.1,81.17)),dot(p,vec2(1269.5,283.37)));return fract(sin(p)*43758.5453);} 
float noise(vec2 p){vec2 i=floor(p),f=fract(p),u=f*f*(3.0-2.0*f);float n=mix(mix(dot(-1.0+2.0*hash(i+vec2(0.0,0.0)),f-vec2(0.0,0.0)),dot(-1.0+2.0*hash(i+vec2(1.0,0.0)),f-vec2(1.0,0.0)),u.x),mix(dot(-1.0+2.0*hash(i+vec2(0.0,1.0)),f-vec2(0.0,1.0)),dot(-1.0+2.0*hash(i+vec2(1.0,1.0)),f-vec2(1.0,1.0)),u.x),u.y);return 0.5+0.5*n;}
void mainImage(out vec4 o, vec2 C){
  float t=iTime*uTimeSpeed;
  vec2 uv=C/iResolution.xy;
  float ratio=iResolution.x/iResolution.y;
  vec2 tuv=uv-0.5+uCenterOffset;
  tuv/=max(uZoom,0.001);

  float degree=noise(vec2(t*0.1,tuv.x*tuv.y)*uNoiseScale);
  tuv.y*=1.0/ratio;
  tuv*=Rot(radians((degree-0.5)*uRotationAmount+180.0));
  tuv.y*=ratio;

  float frequency=uWarpFrequency;
  float ws=max(uWarpStrength,0.001);
  float amplitude=uWarpAmplitude/ws;
  float warpTime=t*uWarpSpeed;
  tuv.x+=sin(tuv.y*frequency+warpTime)/amplitude;
  tuv.y+=sin(tuv.x*(frequency*1.5)+warpTime)/(amplitude*0.5);

  vec3 colLav=uColor1;
  vec3 colOrg=uColor2;
  vec3 colDark=uColor3;
  float b=uColorBalance;
  float s=max(uBlendSoftness,0.0);
  mat2 blendRot=Rot(radians(uBlendAngle));
  float blendX=(tuv*blendRot).x;
  float edge0=-0.3-b-s;
  float edge1=0.2-b+s;
  float v0=0.5-b+s;
  float v1=-0.3-b-s;
  vec3 layer1=mix(colDark,colOrg,S(edge0,edge1,blendX));
  vec3 layer2=mix(colOrg,colLav,S(edge0,edge1,blendX));
  vec3 col=mix(layer1,layer2,S(v0,v1,tuv.y));

  vec2 grainUv=uv*max(uGrainScale,0.001);
  if(uGrainAnimated>0.5){grainUv+=vec2(iTime*0.05);} 
  float grain=fract(sin(dot(grainUv,vec2(12.9898,78.233)))*43758.5453);
  col+=(grain-0.5)*uGrainAmount;

  col=(col-0.5)*uContrast+0.5;
  float luma=dot(col,vec3(0.2126,0.7152,0.0722));
  col=mix(vec3(luma),col,uSaturation);
  col=pow(max(col,0.0),vec3(1.0/max(uGamma,0.001)));
  col=clamp(col,0.0,1.0);

  o=vec4(col,1.0);
}
void main(){
  vec4 o=vec4(0.0);
  mainImage(o,gl_FragCoord.xy);
  fragColor=o;
}
`

const TheGradient = ({
  timeSpeed = 0.25,
  colorBalance = 0.0,
  warpStrength = 1.0,
  warpFrequency = 5.0,
  warpSpeed = 2.0,
  warpAmplitude = 50.0,
  blendAngle = 0.0,
  blendSoftness = 0.05,
  rotationAmount = 500.0,
  noiseScale = 2.0,
  grainAmount = 0.1,
  grainScale = 2.0,
  grainAnimated = false,
  contrast = 1.5,
  gamma = 1.0,
  saturation = 1.0,
  centerX = 0.0,
  centerY = 0.0,
  zoom = 0.9,
  color1 = "#FF9FFC",
  color2 = "#5227FF",
  color3 = "#B19EEF",
  className = "",
}: gradientProps) => {
  const containerRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    if (!containerRef.current) return

    const renderer = new Renderer({
      webgl: 2,
      alpha: true,
      antialias: false,
      dpr: Math.min(window.devicePixelRatio || 1, 2),
    })

    const gl = renderer.gl
    const canvas = gl.canvas
    canvas.style.width = "100%"
    canvas.style.height = "100%"
    canvas.style.display = "block"

    const container = containerRef.current
    container.appendChild(canvas)

    const geometry = new Triangle(gl)
    const program = new Program(gl, {
      vertex,
      fragment,
      uniforms: {
        iTime: { value: 0 },
        iResolution: { value: new Float32Array([1, 1]) },
        uTimeSpeed: { value: timeSpeed },
        uColorBalance: { value: colorBalance },
        uWarpStrength: { value: warpStrength },
        uWarpFrequency: { value: warpFrequency },
        uWarpSpeed: { value: warpSpeed },
        uWarpAmplitude: { value: warpAmplitude },
        uBlendAngle: { value: blendAngle },
        uBlendSoftness: { value: blendSoftness },
        uRotationAmount: { value: rotationAmount },
        uNoiseScale: { value: noiseScale },
        uGrainAmount: { value: grainAmount },
        uGrainScale: { value: grainScale },
        uGrainAnimated: { value: grainAnimated ? 1.0 : 0.0 },
        uContrast: { value: contrast },
        uGamma: { value: gamma },
        uSaturation: { value: saturation },
        uCenterOffset: { value: new Float32Array([centerX, centerY]) },
        uZoom: { value: zoom },
        uColor1: { value: new Float32Array(colorToRgb(color1)) },
        uColor2: { value: new Float32Array(colorToRgb(color2)) },
        uColor3: { value: new Float32Array(colorToRgb(color3)) },
      },
    })

    const mesh = new Mesh(gl, { geometry, program })

    const setSize = () => {
      const rect = container.getBoundingClientRect()
      const width = Math.max(1, Math.floor(rect.width))
      const height = Math.max(1, Math.floor(rect.height))
      renderer.setSize(width, height)
      const res = program.uniforms.iResolution.value
      res[0] = gl.drawingBufferWidth
      res[1] = gl.drawingBufferHeight
    }

    const ro = new ResizeObserver(setSize)
    ro.observe(container)
    setSize()

    let raf = 0
    const t0 = performance.now()
    const loop = (t: number) => {
      program.uniforms.iTime.value = (t - t0) * 0.001
      renderer.render({ scene: mesh })
      raf = requestAnimationFrame(loop)
    }
    raf = requestAnimationFrame(loop)

    return () => {
      cancelAnimationFrame(raf)
      ro.disconnect()
      try {
        container.removeChild(canvas)
      } catch {
        // Ignore
      }
    }
  }, [
    timeSpeed,
    colorBalance,
    warpStrength,
    warpFrequency,
    warpSpeed,
    warpAmplitude,
    blendAngle,
    blendSoftness,
    rotationAmount,
    noiseScale,
    grainAmount,
    grainScale,
    grainAnimated,
    contrast,
    gamma,
    saturation,
    centerX,
    centerY,
    zoom,
    color1,
    color2,
    color3,
  ])

  return (
    <div
      ref={containerRef}
      className={`gradient-container ${className}`.trim()}
    />
  )
}

export default TheGradient
